import { rd } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { Building2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Selection } from 'react-aria-components';
import type { Resource } from '__types__';
import { MountElementProps } from 'v5/messages/mount-element';
import { renderError } from 'v5/patterns/widgets/renderError';
import { Button } from 'v5/design-system/Button';
import {
  ListBox,
  ListBoxHeader,
  ListBoxItem,
  ListBoxItemContent,
  ListBoxSkeleton
} from 'v5/design-system/ListBox';
import {
  Dialog,
  DialogContentHeadless,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from 'v5/design-system/Dialog';
import { Label } from 'v5/design-system/Label';
import { RadioGroup, RadioGroupItem } from 'v5/design-system/RadioGroup';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { UnsavedChangesProtection } from 'v5/patterns/forms/unsaved-changes/UnsavedChangesProtection';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { getUserFacingErrorMessage } from 'v5/platform/lang/getUserFacingErrorMessage';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';
import { WithServices } from 'v5/platform/ts/services';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';

/**
 * Membership ids for assign/read:
 * - [] → hidden
 * - [null] → global
 * - [1, 2, …] → workspaces
 */
export type ResourceWorkspaceMembershipIds = Array<number | null>;

export type AssignScopeMode = 'hidden' | 'global' | 'workspaces';

export type AssignResourcesToWorkspacesDialogProps = MountElementProps &
  WithServices<[WithEnterpriseService]> & {
    organizationId: string;
    resourceName: string;
    resourceCount?: number;
    /** Membership from the caller (list/detail row). */
    initialWorkspaceIds?: ResourceWorkspaceMembershipIds;
    onSubmit: (workspaceIds: ResourceWorkspaceMembershipIds) => Promise<void>;
  };

function sameIdList(
  a: ResourceWorkspaceMembershipIds,
  b: ResourceWorkspaceMembershipIds
) {
  if (a.length !== b.length) return false;
  return a.every((id, index) => id === b[index]);
}

function normalizeMembershipIds(
  ids: ResourceWorkspaceMembershipIds
): ResourceWorkspaceMembershipIds {
  if (ids.length === 1 && ids[0] === null) {
    return [null];
  }
  return [
    ...new Set(
      ids
        .filter((id): id is number => id != null && Number.isFinite(Number(id)))
        .map(Number)
    )
  ].sort((a, b) => a - b);
}

export function membershipMode(
  ids: ResourceWorkspaceMembershipIds
): AssignScopeMode {
  const normalized = normalizeMembershipIds(ids);
  if (normalized.length === 0) return 'hidden';
  if (normalized.length === 1 && normalized[0] === null) return 'global';
  return 'workspaces';
}

function membershipIdsFromResource(
  resource: Pick<Resource, 'enterprise_workspaces'>
): ResourceWorkspaceMembershipIds {
  return normalizeMembershipIds(
    (resource.enterprise_workspaces ?? []).map(
      entry => entry.enterprise_workspace_id
    )
  );
}

/** Shared membership across resources; mixed modes → shared numeric ids only. */
export function sharedEnterpriseWorkspaceIds(
  resources: Array<Pick<Resource, 'enterprise_workspaces'>>
): ResourceWorkspaceMembershipIds {
  if (resources.length === 0) return [null];
  const lists = resources.map(membershipIdsFromResource);
  const modes = new Set(lists.map(membershipMode));
  if (modes.size === 1) {
    const mode = [...modes][0]!;
    if (mode === 'hidden') return [];
    if (mode === 'global') return [null];
    const sets = lists.map(
      ids => new Set(ids.filter((id): id is number => id != null))
    );
    const [first, ...rest] = sets;
    return [...first!]
      .filter(id => rest.every(set => set.has(id)))
      .sort((a, b) => a - b);
  }
  const sets = lists.map(
    ids => new Set(ids.filter((id): id is number => id != null))
  );
  const [first, ...rest] = sets;
  return [...first!]
    .filter(id => rest.every(set => set.has(id)))
    .sort((a, b) => a - b);
}

export function AssignResourcesToWorkspacesDialog({
  open,
  onOpenChange,
  organizationId,
  resourceName,
  resourceCount,
  initialWorkspaceIds = [null],
  onSubmit,
  services
}: AssignResourcesToWorkspacesDialogProps) {
  const { t } = useTranslation('resources', {
    keyPrefix: 'AssignResourcesToWorkspacesDialog'
  });
  const baseline = normalizeMembershipIds(initialWorkspaceIds);

  return (
    <UnsavedChangesProtection
      open={open}
      onOpenChange={onOpenChange}
      beforeUnloadMessage={t('unsavedChangesBeforeUnload')}
    >
      {dialogProps => (
        <Dialog {...dialogProps}>
          <DialogContentHeadless className="max-w-120">
            <AssignWorkspacesForm
              key={membershipKey(baseline)}
              open={dialogProps.open}
              organizationId={organizationId}
              resourceName={resourceName}
              resourceCount={resourceCount}
              baseline={baseline}
              onSubmit={onSubmit}
              services={services}
            />
          </DialogContentHeadless>
        </Dialog>
      )}
    </UnsavedChangesProtection>
  );
}

function membershipKey(ids: ResourceWorkspaceMembershipIds) {
  return normalizeMembershipIds(ids)
    .map(id => (id === null ? 'null' : String(id)))
    .join(',');
}

type FormProps = WithServices<[WithEnterpriseService]> & {
  open: boolean;
  organizationId: string;
  resourceName: string;
  resourceCount?: number;
  baseline: ResourceWorkspaceMembershipIds;
  onSubmit: (workspaceIds: ResourceWorkspaceMembershipIds) => Promise<void>;
};

function AssignWorkspacesForm({
  open,
  organizationId,
  resourceName,
  resourceCount,
  baseline,
  onSubmit,
  services
}: FormProps) {
  const { t } = useTranslation('resources', {
    keyPrefix: 'AssignResourcesToWorkspacesDialog'
  });
  const [mode, setMode] = useState<AssignScopeMode>(() =>
    membershipMode(baseline)
  );
  const [selectedIds, setSelectedIds] = useState<number[]>(() =>
    membershipMode(baseline) === 'workspaces'
      ? (normalizeMembershipIds(baseline).filter(
          (id): id is number => id != null
        ) as number[])
      : []
  );

  const currentIds: ResourceWorkspaceMembershipIds =
    mode === 'hidden'
      ? []
      : mode === 'global'
        ? [null]
        : normalizeMembershipIds(selectedIds);

  const mutation = promiseState.useMutation(
    async (workspaceIds: ResourceWorkspaceMembershipIds) => {
      await onSubmit(workspaceIds);
    }
  );

  useRegisterDirtyState(
    !sameIdList(
      normalizeMembershipIds(currentIds),
      normalizeMembershipIds(baseline)
    )
  );

  const workspacesRd = services.enterpriseService.useWorkspaces(
    organizationId,
    'active'
  );

  const workspaces = rd.tryGet(workspacesRd) ?? [];

  const selectedKeys = useMemo(
    () => new Set(selectedIds.map(id => String(id))),
    [selectedIds]
  );

  const handleWorkspaceSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      setSelectedIds(workspaces.map(workspace => Number(workspace.id)));
      return;
    }
    setSelectedIds(
      Array.from(keys)
        .map(key => Number(key))
        .filter(id => Number.isFinite(id))
    );
  };

  const canSave = mode !== 'workspaces' || selectedIds.length > 0;

  return (
    <FormDialogContent
      open={open}
      className="max-w-120"
      title={t('title')}
      onSubmit={() => {
        if (!canSave) return;
        mutation.track(currentIds);
      }}
      mutation={mutation.state}
      saveText={t('save')}
      disabled={!canSave}
      footerContent={
        <DialogErrorFooter
          compact
          mutation={mutation.state}
          renderError={error =>
            getUserFacingErrorMessage(error) || t('assignFailed')
          }
        />
      }
      saveButtonSlot={
        <AsyncButton
          type="submit"
          mutation={mutation.state}
          variant="primary"
          className="shrink-0 self-center"
          leftIcon={<Building2 className="size-4" />}
          disabled={!canSave}
        >
          {t('save')}
        </AsyncButton>
      }
      renderHeader={title => (
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {t('description', { count: resourceCount, name: resourceName })}
          </DialogDescription>
        </DialogHeader>
      )}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-base font-medium text-fg-default">
            {t('fieldScope')}
          </p>
          <RadioGroup
            value={mode}
            onValueChange={value => {
              if (
                value === 'hidden' ||
                value === 'global' ||
                value === 'workspaces'
              ) {
                setMode(value);
              }
            }}
            className="gap-3"
          >
            <div className="flex items-start gap-2.5">
              <RadioGroupItem value="hidden" id="assign-scope-hidden" />
              <div className="min-w-0 space-y-0.5">
                <Label htmlFor="assign-scope-hidden" className="cursor-pointer">
                  {t('scopeHidden')}
                </Label>
                <p className="text-sm text-fg-muted">{t('scopeHiddenHint')}</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <RadioGroupItem value="global" id="assign-scope-global" />
              <div className="min-w-0 space-y-0.5">
                <Label htmlFor="assign-scope-global" className="cursor-pointer">
                  {t('scopeGlobal')}
                </Label>
                <p className="text-sm text-fg-muted">{t('scopeGlobalHint')}</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <RadioGroupItem value="workspaces" id="assign-scope-workspaces" />
              <Label
                htmlFor="assign-scope-workspaces"
                className="cursor-pointer"
              >
                {t('scopeWorkspaces')}
              </Label>
            </div>
          </RadioGroup>
        </div>

        {mode === 'workspaces' && (
          <div className="space-y-2">
            <p className="text-base font-medium text-fg-default">
              {t('fieldWorkspaces')}
            </p>
            <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-border-default">
              <ListBoxHeader>
                <span className="text-sm text-fg-muted">
                  {selectedIds.length > 0
                    ? t('workspacesSelectedCount', {
                        count: selectedIds.length
                      })
                    : t('workspacesSelectPrompt')}
                </span>
                {selectedIds.length > 0 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="-my-2"
                    onClick={() => setSelectedIds([])}
                  >
                    {t('workspacesUnselectAll')}
                  </Button>
                ) : null}
              </ListBoxHeader>
              <ScrollShadowWrapper className="max-h-72 min-h-0 overflow-y-auto">
                {rd
                  .journey(workspacesRd)
                  .wait(<ListBoxSkeleton numRows={4} showExtra={false} />)
                  .catch(renderError)
                  .map(loadedWorkspaces =>
                    loadedWorkspaces.length === 0 ? (
                      <div className="p-6 text-center text-sm text-fg-muted">
                        {t('workspacesEmpty')}
                      </div>
                    ) : (
                      <ListBox
                        aria-label={t('fieldWorkspaces')}
                        selectionMode="multiple"
                        selectedKeys={selectedKeys}
                        onSelectionChange={handleWorkspaceSelectionChange}
                      >
                        {loadedWorkspaces.map(workspace => {
                          const workspaceId = Number(workspace.id);
                          return (
                            <ListBoxItem
                              key={workspaceId}
                              id={String(workspaceId)}
                              textValue={workspace.name}
                            >
                              <ListBoxItemContent
                                icon={
                                  <EnterpriseWorkspaceIcon
                                    name={workspace.name}
                                    iconUrl={workspace.iconUrl}
                                  />
                                }
                                primary={workspace.name}
                              />
                            </ListBoxItem>
                          );
                        })}
                      </ListBox>
                    )
                  )}
              </ScrollShadowWrapper>
            </div>
          </div>
        )}
      </div>
    </FormDialogContent>
  );
}
